import React from 'react'
import "../common/LicenseDetail.css"
import { useState, useEffect } from 'react'
import EditDetails from '../common/EditDetails';
import editIcon from "./../../../../assets/img/icon/editIcon.svg";
import { useUpdateLicenseType, useGetApplicationTypes } from "../../../../backend/license";
import { useGetStates, useGetCities } from "../../../../backend/masters";
import { hasUserAccess, getUserDetails } from "../../../../backend/auth";
import { toast } from "react-toastify";
import Icon from "../../../common/Icon";
import { USER_PRIVILEGES } from "../../UserManagement/Roles/RoleConfiguration";

interface LicenseTypeDetailProps {
  isOpen: boolean;
  closePanel: () => void;
  license?: any;
  refetch?: () => void;
}

const LicenseTypeDetail : React.FC<LicenseTypeDetailProps> = ({isOpen ,closePanel,license, refetch}) => {
  const [isEditOpen, setIsEditOpen] = useState(false);
  const user = getUserDetails();

  const { updateLicenseType, updating } = useUpdateLicenseType(
    (response: any) => {
      (response &&
      toast.success("License type updated successfully", {
        icon: <Icon name="snackbarSuccess" />,
      }))
      closeEditPopup();
      closePanel();
      if (refetch) refetch();
    },
    (error: any) => {
      const errorData = error && error.response && error.response.data;
      const errorMessages = errorData && errorData.error_messages;

      if (errorMessages && Array.isArray(errorMessages) && errorMessages.length > 0) {
        errorMessages.forEach((errMsg: string) => {
          toast.error(errMsg, {
            icon: <Icon name="snackbarError" />,
          });
        });
      } else if (errorData && errorData.message) {
        toast.error(errorData.message);
      } else {
        toast.error("Failed to update license type");
      }
    }
  );

  // Get states and cities for the form
  const { states } = useGetStates({}, true);
  const [selectedStateId, setSelectedStateId] = useState<string>("");
  const { cities } = useGetCities(
    { filters: selectedStateId ? { state_id_eq: selectedStateId } : {} },
    Boolean(selectedStateId)
  );

  useEffect(() => {
    if (license && license.state_id) {
      setSelectedStateId(license.state_id.toString());
    }
  }, [license]);

  // Fetch application types (SOPs) for this license type
  const {
    applicationTypes: applicationTypesWithSOP,
  } = useGetApplicationTypes(
    {
      filters: {
        license_type_id_eq: license && license.id ? license.id : undefined,
      },
    },
    !!(license && license.id)
  );

  const openEditPopup = () => setIsEditOpen(true);
  const closeEditPopup = () => setIsEditOpen(false);

  const handleSave = (data: any) => {
    if (!license || !license.id) return;

    let updatedData = { ...data };
    if (data.name && data.name !== license.name) {
      const licenseCode = data.name
        .toUpperCase()
        .replace(/[^A-Z0-9]+/g, "_")
        .replace(/^_|_$/g, "")
        .substring(0, 50) || "LICENSE_TYPE";
      updatedData.code = licenseCode;
    }
    const payload = {
      id: license.id,
      license_type: {
        ...updatedData,
        updated_by_id: user.user_id,
      },
    };
    updateLicenseType(payload as any);
  };

  const baseFields = license ? [
    { label: "License Type Name", value: license.name || "-" },
    { label: "Is Perpetual", value: license.perpetual_allowed ? "Yes" : "No" },
    { label: "Scope", value: license.scope ? license.scope : "-" },
    { label: "State", value: (license.state && license.state.name) || "-" },
    { label: "City", value: (license.city && license.city.name) || "-" },
    { label: "Authority", value: license.authority || "-" },
    { label: "Company", value: (license.company && license.company.name) || "-" },
    { label: "Associate Company", value: (license.associate_company && license.associate_company.name) || "-" },
    { label: "Is Active", value: license.is_active ? "Yes" : "No" },
  ] : [];

  const extraFields = license ? [
    { label: "License Type Name", value: license.name || "-" },
    { label: "Is Perpetual", value: license.perpetual_allowed ? "Yes" : "No" },
    { label: "Scope", value: license.scope ? license.scope : "-" },
    { label: "State", value: (license.state && license.state.name) || "-" },
    { label: "City", value: (license.city && license.city.name) || "-" },
    { label: "Authority", value: license.authority || "-" },
    { label: "Company", value: (license.company && license.company.name) || "-" },
    { label: "Associate Company", value: (license.associate_company && license.associate_company.name) || "-" },
    { label: "Is Active", value: license.is_active ? "Yes" : "No" },
    { label: "Default Validity Days", value: license.default_validity_days || "-" },
    { label: "Up for Renewal Days", value: license.up_for_renewal_days || "-" },
    { label: "Number of Renewal Reminders", value: license.renewal_reminder_count || "-" },
    ...(license.reminder_days && license.reminder_days.length > 0 ? license.reminder_days.map((days: number, index: number) => ({
      label: `Reminder ${index + 1} to be sent (days before expiry)`,
      value: days.toString()
    })) : [])
  ] : [];

  const fieldsToDisplay = license && license.perpetual_allowed ? baseFields : extraFields;

  if (!license) {
    return null;
  }
  
  return (
    <>
      {isOpen && (
        <div className="licensedetails-overlay" onClick={closePanel}></div>
      )}
      <div className="licensedetails-wrapper">
        <div className={`licensedetails licensetypedetails ${isOpen ? "open" : ""}`}>
          <div className="licensedetails-header d-flex justify-content-between align-items-center pb-2">
            <h5 className="mb-0 fw-semibold">License Type Details</h5>
            <div className="d-flex justify-content-between align-items-center gap-4">
              {hasUserAccess(USER_PRIVILEGES.CREATE_AND_EDIT_LICENSE_APPLICATION) &&
                <div className="d-flex align-items-center gap-2 cursor-pointer" onClick={openEditPopup}>
                  <img src={editIcon} alt="ededitIcont" />
                  <span className='edit_button'>Edit</span>
                </div>
              }
              <button
                className="btn-close"
                aria-label="Close"
                onClick={closePanel}
              ></button>
            </div>
          </div>
          <hr />
          <div className="licensedetails-body h-100">
            <ul className="licensedetails-text pb-1">
              {fieldsToDisplay.map((item, index) => (
                <li key={index}>
                  <div>{item.label}</div>
                  <div>{item.value}</div>
                </li>
              ))}
              {applicationTypesWithSOP && applicationTypesWithSOP.length > 0 ? (
                applicationTypesWithSOP.map((appType: any) => (
                  <li key={appType.id}>
                    <div><label className="mb-2 fw-semibold">
                        Standard Operating Procedure for {appType.action_type === 'new' ? 'New' :
                                appType.action_type === 'renewal' ? 'Renewal' :
                                appType.action_type === 'amendment' ? 'Amendment' :
                                appType.action_type === 'surrender' ? 'Surrender' :
                                appType.action_type} (view only)
                      </label></div>
                    <div className='w-100'>                      
                      <div className='w-100 licensetypedetails-textarea' style={{ whiteSpace: 'pre-wrap' }}>
                        {appType.sop || 'No Standard Operating Procedure provided'}
                      </div>
                    </div>
                  </li>
                ))
              ) : (
                <li>
                  <div></div>
                  <div className='w-100 licensetypedetails-textarea'>
                    <p className="text-muted">No Standard Operating Procedure&apos;s available for this license type</p>
                  </div>
                </li>
              )}
            </ul>
          </div>
          {license && (
            <EditDetails
              isOpen={isEditOpen}
              onClose={closeEditPopup}
              activeSection="license-type"
              initialData={{
                name: license.name || "",
                is_perpetual: license.perpetual_allowed || false,
                default_validity_days: license.default_validity_days || "",
                renewal_days: license.up_for_renewal_days || "",
                scope: license.scope || "",
                state_id: license.state_id || "",
                city_id: license.city_id || "",
                authority: license.authority || "",
                renewal_reminder_count: license.renewal_reminder_count || 3,
                reminder_days: license.reminder_days || [],
                existing_sops: applicationTypesWithSOP && applicationTypesWithSOP.length > 0 ? applicationTypesWithSOP.map((appType: any) => ({
                  action_type: appType.action_type,
                  sop: appType.sop || "",
                  id: appType.id
                })) : [],
              }}
              onSave={handleSave}
              isLoading={updating}
              selectState={states || []}
              selectCity={cities || []}
              onStateChange={(stateId) => setSelectedStateId(stateId)}
            />
          )}
        </div>
      </div>
    </>
  );
}

export default LicenseTypeDetail;